import {
    buildMockupDataUrl,
    resolveCanvasBackground,
    resolveCatalogPreviewUrl,
} from "./mockup";
import type {
    AiImageResponse,
    AiImageProvider,
    DesignerBootstrap,
    DesignerColor,
    DesignerMode,
    DesignerObject,
    DesignerProduct,
    LayerType,
    MessageResponse,
    ProjectItemResponse,
    UploadResponse,
} from "./types";

const layerProps = ["mode", "layerId", "layerType", "layerName", "assetPath"];
const fonts = ["Trebuchet MS", "Georgia", "Garamond", "Courier New", "Impact"];
const canvasBaseWidth = 520;
const canvasBaseHeight = 620;
const supportedLanguages = ["ms", "en"] as const;
type DesignerLanguage = (typeof supportedLanguages)[number];
type DesignerView = "catalog" | "editor";
const defaultLanguage: DesignerLanguage = "en";

interface DesignerElements {
    addTextButton: HTMLButtonElement;
    artworkCountBack: HTMLSpanElement;
    artworkCountFront: HTMLSpanElement;
    artworkCountTotal: HTMLSpanElement;
    addToCartButton: HTMLButtonElement;
    aiProviderSelect: HTMLSelectElement | null;
    aiPromptInput: HTMLTextAreaElement | null;
    aiStyleSelect: HTMLSelectElement | null;
    backToCatalogButton: HTMLButtonElement;
    bringForwardButton: HTMLButtonElement;
    catalogView: HTMLElement;
    canvasStage: HTMLDivElement;
    catalogCards: HTMLDivElement;
    colorSwatches: HTMLDivElement;
    currentColorLabel: HTMLSpanElement;
    currentModeLabel: HTMLSpanElement;
    currentProductLabel: HTMLSpanElement;
    downloadPreviewButton: HTMLButtonElement;
    duplicateLayerButton: HTMLButtonElement;
    editorView: HTMLElement;
    fillColorInput: HTMLInputElement;
    fontFamilySelect: HTMLSelectElement;
    fontFamilyWrap: HTMLDivElement;
    fontSizeInput: HTMLInputElement;
    fontSizeWrap: HTMLDivElement;
    generateAiButton: HTMLButtonElement | null;
    imageUploadInput: HTMLInputElement;
    layerTextInput: HTMLTextAreaElement;
    languageSelect: HTMLSelectElement | null;
    layersList: HTMLDivElement;
    modeButtons: HTMLButtonElement[];
    newProjectButton: HTMLButtonElement;
    opacityInput: HTMLInputElement;
    pageHeader: HTMLElement;
    printAreaGuide: HTMLDivElement;
    projectNameInput: HTMLInputElement;
    removeLayerButton: HTMLButtonElement;
    rotationInput: HTMLInputElement;
    saveProjectButton: HTMLButtonElement;
    selectedLayerType: HTMLSpanElement;
    selectionControls: HTMLDivElement;
    selectionEmpty: HTMLDivElement;
    sendBackwardButton: HTMLButtonElement;
    textFieldWrap: HTMLDivElement;
    textStyleGroup: HTMLDivElement;
    toast: HTMLDivElement;
}

interface DesignerState {
    canvas: FabricCanvas | null;
    currentProjectId: number | null;
    mode: DesignerMode;
    product: DesignerProduct | null;
    color: DesignerColor | null;
    products: DesignerProduct[];
    selectedObject: DesignerObject | null;
    toastTimer: number | null;
    view: DesignerView;
}

let bootstrap: DesignerBootstrap;
let elements: DesignerElements;
let state: DesignerState;

export function bootDesigner(): void {
    const bootstrapElement = document.getElementById("designer-bootstrap");

    if (!bootstrapElement || typeof fabric === "undefined") {
        return;
    }

    bootstrap = parseBootstrapPayload(bootstrapElement);
    elements = getElements();
    state = {
        canvas: null,
        currentProjectId: null,
        mode: "front",
        product: null,
        color: null,
        products: Array.isArray(bootstrap.products) ? bootstrap.products : [],
        selectedObject: null,
        toastTimer: null,
        view: "catalog",
    };

    init();
}

function init(): void {
    initLanguageSelect();
    initAiProviderSelect();

    if (!state.products.length) {
        showToast(t("noProductsConfigured"), true);
        return;
    }

    const requestedProduct = findProductByKey(getRequestedProductKey());
    const requestedProjectId = getRequestedProjectId();
    const initialProduct =
        requestedProduct
        ?? state.products.find((product) => product.colors.length > 0)
        ?? state.products[0];
    const initialView: DesignerView =
        (bootstrap.customer.isAuthenticated && requestedProjectId !== null)
        || (getRequestedView() === "editor" && requestedProduct !== null)
            ? "editor"
            : "catalog";

    populateFontOptions();
    setupCanvas();
    syncArtworkSummary();
    bindEvents();
    setProduct(initialProduct.key, {
        preferredColorKey: getRequestedColorKey() ?? undefined,
        syncUrl: false,
    });

    if (initialView === "editor") {
        showEditorView({
            syncUrl: false,
        });
    } else {
        showCatalogView({
            syncUrl: false,
        });
    }

    void loadRequestedProjectFromUrl();
}

function populateFontOptions(): void {
    elements.fontFamilySelect.innerHTML = fonts
        .map((font) => `<option value="${font}">${font}</option>`)
        .join("");
}

function setupCanvas(): void {
    state.canvas = new fabric.Canvas("designer-canvas", {
        width: canvasBaseWidth,
        height: canvasBaseHeight,
        preserveObjectStacking: true,
        selection: false,
    });

    state.canvas.on("selection:created", handleSelectionChange);
    state.canvas.on("selection:updated", handleSelectionChange);
    state.canvas.on("selection:cleared", clearSelection);
    state.canvas.on("object:added", () => {
        refreshLayerList();
        syncArtworkSummary();
    });
    state.canvas.on("object:removed", () => {
        refreshLayerList();
        syncArtworkSummary();
    });
    state.canvas.on("object:modified", (event) => {
        if (event.target) {
            const target = event.target as DesignerObject;

            decorateObject(target);
            constrainObjectToPrintArea(target);
        }

        renderCanvas();
        syncSelectionControls();
        refreshLayerList();
        syncArtworkSummary();
    });

    syncCanvasViewport();
}

function bindEvents(): void {
    elements.addTextButton.addEventListener("click", addTextLayer);
    elements.backToCatalogButton.addEventListener("click", () => {
        showCatalogView();
    });
    elements.languageSelect?.addEventListener("change", () => {
        applyLanguageSelection(elements.languageSelect?.value ?? bootstrap.locale, {
            reload: true,
        });
    });
    elements.newProjectButton.addEventListener("click", resetDesign);
    elements.saveProjectButton.addEventListener("click", () => {
        void saveProject();
    });
    elements.addToCartButton.addEventListener("click", () => {
        void addCurrentProjectToCart();
    });
    elements.downloadPreviewButton.addEventListener("click", () => {
        void downloadPreview();
    });

    elements.imageUploadInput.addEventListener("change", (event) => {
        void handleArtworkUpload(event);
    });

    elements.generateAiButton?.addEventListener("click", () => {
        void generateAiArtwork();
    });

    elements.modeButtons.forEach((button) => {
        button.addEventListener("click", () => {
            const mode = button.dataset.mode;

            if (mode === "front" || mode === "back") {
                setMode(mode);
            }
        });
    });

    elements.catalogCards.addEventListener("click", (event) => {
        const card = closestFromEvent<HTMLButtonElement>(event, "[data-product-key]");
        const productKey = card?.dataset.productKey;

        if (productKey) {
            setProduct(productKey);
            showEditorView();
        }
    });

    elements.colorSwatches.addEventListener("click", (event) => {
        const button = closestFromEvent<HTMLButtonElement>(event, "[data-color-key]");
        const colorKey = button?.dataset.colorKey;

        if (colorKey) {
            setColor(colorKey);
        }
    });

    elements.layersList.addEventListener("click", (event) => {
        const action = closestFromEvent<HTMLButtonElement>(event, "[data-action]");

        if (action?.dataset.action && action.dataset.layerId) {
            if (action.dataset.action === "duplicate") {
                duplicateLayer(action.dataset.layerId);
            }

            if (action.dataset.action === "remove") {
                removeLayer(action.dataset.layerId);
            }

            return;
        }

        const layerButton = closestFromEvent<HTMLButtonElement>(event, "[data-layer-id]");
        const layerId = layerButton?.dataset.layerId;

        if (layerId) {
            selectLayer(layerId);
        }
    });

    elements.layerTextInput.addEventListener("input", () => {
        if (!isTextSelection()) {
            return;
        }

        state.selectedObject?.set("text", elements.layerTextInput.value || " ");
        renderCanvas();
        refreshLayerList();
    });

    elements.fontFamilySelect.addEventListener("change", () => {
        if (!isTextSelection()) {
            return;
        }

        state.selectedObject?.set("fontFamily", elements.fontFamilySelect.value);
        renderCanvas();
    });

    elements.fillColorInput.addEventListener("input", () => {
        if (!isTextSelection()) {
            return;
        }

        state.selectedObject?.set("fill", elements.fillColorInput.value);
        renderCanvas();
    });

    elements.fontSizeInput.addEventListener("input", () => {
        if (!isTextSelection()) {
            return;
        }

        state.selectedObject?.set("fontSize", Number(elements.fontSizeInput.value));
        renderCanvas();
    });

    elements.opacityInput.addEventListener("input", () => {
        if (!state.selectedObject) {
            return;
        }

        state.selectedObject.set("opacity", Number(elements.opacityInput.value) / 100);
        renderCanvas();
    });

    elements.rotationInput.addEventListener("input", () => {
        if (!state.selectedObject) {
            return;
        }

        state.selectedObject.rotate(Number(elements.rotationInput.value));
        renderCanvas();
    });

    elements.duplicateLayerButton.addEventListener("click", () => {
        if (state.selectedObject?.layerId) {
            duplicateLayer(state.selectedObject.layerId);
        }
    });

    elements.removeLayerButton.addEventListener("click", () => {
        if (state.selectedObject?.layerId) {
            removeLayer(state.selectedObject.layerId);
        }
    });

    elements.bringForwardButton.addEventListener("click", () => {
        if (!state.canvas || !state.selectedObject) {
            return;
        }

        state.canvas.bringForward(state.selectedObject);
        renderCanvas();
        refreshLayerList();
    });

    elements.sendBackwardButton.addEventListener("click", () => {
        if (!state.canvas || !state.selectedObject) {
            return;
        }

        state.canvas.sendBackwards(state.selectedObject);
        renderCanvas();
        refreshLayerList();
    });

    document.addEventListener("keydown", (event) => {
        if (!state.selectedObject?.layerId) {
            return;
        }

        if (!["Delete", "Backspace"].includes(event.key)) {
            return;
        }

        const activeElement = document.activeElement;

        if (
            activeElement instanceof HTMLElement &&
            ["INPUT", "TEXTAREA", "SELECT"].includes(activeElement.tagName)
        ) {
            return;
        }

        removeLayer(state.selectedObject.layerId);
    });

    if ("ResizeObserver" in window) {
        const observer = new ResizeObserver(() => {
            syncCanvasViewport();
        });

        observer.observe(elements.canvasStage);
    } else {
        globalThis.addEventListener("resize", syncCanvasViewport);
    }
}

async function handleArtworkUpload(event: Event): Promise<void> {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
        return;
    }

    const [file] = Array.from(target.files ?? []);

    if (!file) {
        return;
    }

    await uploadArtwork(file);
    target.value = "";
}

function setProduct(
    productKey: string,
    options: {
        preferredColorKey?: string;
        repositionLayers?: boolean;
        syncUrl?: boolean;
    } = {},
): void {
    const previousProduct = state.product;
    const product = state.products.find((entry) => entry.key === productKey);

    if (!product) {
        return;
    }

    state.product = product;

    const preferredColorKey = options.preferredColorKey ?? state.color?.key;
    state.color =
        product.colors.find((entry) => entry.key === preferredColorKey) ?? product.colors[0] ?? null;

    if (
        options.repositionLayers !== false
        && previousProduct
        && previousProduct.key !== product.key
    ) {
        repositionLayersForPrintArea(previousProduct.printArea, product.printArea);
    }

    renderCatalog();
    renderColorSwatches();
    updateMeta();
    applyBackground();
    applyModeVisibility();
    updatePrintAreaGuide();

    if (options.syncUrl !== false && state.view === "editor") {
        syncDesignerUrl();
    }
}

function setColor(colorKey: string): void {
    if (!state.product) {
        return;
    }

    const color = state.product.colors.find((entry) => entry.key === colorKey);

    if (!color) {
        return;
    }

    state.color = color;
    renderColorSwatches();
    updateMeta();
    applyBackground();

    if (state.view === "editor") {
        syncDesignerUrl();
    }
}

function setMode(mode: DesignerMode): void {
    if (state.mode === mode) {
        return;
    }

    state.mode = mode;
    updateModeButtons();
    updateMeta();
    applyBackground();
    applyModeVisibility();
    updatePrintAreaGuide();
}

function showCatalogView(
    options: {
        syncUrl?: boolean;
    } = {},
): void {
    state.view = "catalog";
    elements.pageHeader.classList.remove("editor-active");
    elements.catalogView.classList.remove("hidden");
    elements.editorView.classList.add("hidden");

    if (options.syncUrl !== false) {
        syncDesignerUrl();
    }
}

function showEditorView(
    options: {
        syncUrl?: boolean;
    } = {},
): void {
    state.view = "editor";
    elements.pageHeader.classList.add("editor-active");
    elements.catalogView.classList.add("hidden");
    elements.editorView.classList.remove("hidden");
    syncCanvasViewport();
    updateModeButtons();
    updateMeta();
    applyBackground();
    applyModeVisibility();
    updatePrintAreaGuide();

    if (options.syncUrl !== false) {
        syncDesignerUrl();
    }
}

function updateModeButtons(): void {
    elements.modeButtons.forEach((button) => {
        const isActive = button.dataset.mode === state.mode;
        button.classList.toggle("active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
}

function updateMeta(): void {
    elements.currentProductLabel.textContent = state.product?.name ?? "";
    elements.currentColorLabel.textContent = state.color?.name ?? "";
    elements.currentModeLabel.textContent = t(state.mode);
}

function applyBackground(): void {
    if (!state.product || !state.canvas) {
        return;
    }

    const background = resolveCanvasBackground(
        state.product,
        state.color,
        state.mode,
        canvasBaseWidth,
        canvasBaseHeight,
    );

    state.canvas.setBackgroundImage(
        background.src,
        renderCanvas,
        background.options,
    );
}

function applyModeVisibility(): void {
    if (!state.canvas) {
        return;
    }

    state.canvas.getObjects().forEach((object) => {
        (object as DesignerObject).visible = (object as DesignerObject).mode === state.mode;
    });

    if (state.selectedObject && state.selectedObject.mode !== state.mode) {
        state.canvas.discardActiveObject();
        state.selectedObject = null;
    }

    renderCanvas();
    refreshLayerList();
    syncSelectionControls();
}

function addTextLayer(): void {
    if (!state.canvas) {
        return;
    }

    const area = getPrintArea();
    const text = new fabric.Textbox(t("defaultTextContent"), {
        editable: true,
        fill: "#101828",
        fontFamily: "Trebuchet MS",
        fontSize: 34,
        left: area.x + area.width / 2,
        top: area.y + area.height / 2,
        originX: "center",
        originY: "center",
        textAlign: "center",
        width: Math.min(area.width, 220),
    }) as DesignerObject;

    decorateObject(text, "text");
    state.canvas.add(text);
    state.canvas.setActiveObject(text);
    text.enterEditing?.();
    text.selectAll?.();
    renderCanvas();
}

async function uploadArtwork(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("asset", file);

    elements.imageUploadInput.disabled = true;

    try {
        const payload = await request<UploadResponse>(bootstrap.api.uploads, {
            method: "POST",
            body: formData,
        });

        await addImageLayer(payload.url, file.name || payload.name, payload.path);
        showToast(t("artworkUploaded"));
    } catch (error) {
        showToast(asErrorMessage(error), true);
    } finally {
        elements.imageUploadInput.disabled = false;
    }
}

async function generateAiArtwork(): Promise<void> {
    if (
        !elements.aiPromptInput
        || !elements.aiStyleSelect
        || !elements.generateAiButton
    ) {
        showToast(t("aiDisabled"), true);
        return;
    }

    const prompt = elements.aiPromptInput.value.trim();
    const provider = resolveSelectedAiProvider();

    if (!prompt) {
        showToast(t("describeAiPrompt"), true);
        return;
    }

    if (!provider) {
        showToast(t("aiProviderUnavailable"), true);
        return;
    }

    elements.generateAiButton.disabled = true;
    elements.generateAiButton.textContent = t("generatingAiButton");

    try {
        const payload = await request<AiImageResponse>(bootstrap.api.aiImages, {
            method: "POST",
            body: JSON.stringify({
                prompt,
                provider,
                style: elements.aiStyleSelect.value,
                referencePaths: collectAiReferencePaths(),
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });

        await addImageLayer(payload.url, t("aiArtworkLayerName"), payload.path);
        showToast(t("aiArtworkAdded", { provider: payload.providerLabel }));
    } catch (error) {
        showToast(asErrorMessage(error), true);
    } finally {
        elements.generateAiButton.disabled = false;
        elements.generateAiButton.textContent = t("generateAiButton");
    }
}

function addImageLayer(url: string, layerName = t("defaultArtworkLayerName"), assetPath?: string): Promise<void> {
    if (!state.canvas) {
        return Promise.resolve();
    }

    const area = getPrintArea();

    return new Promise((resolve, reject) => {
        fabric.Image.fromURL(
            url,
            (image) => {
                if (!image) {
                    reject(new Error(t("imageLoadFailed")));
                    return;
                }

                const designerImage = image as DesignerObject;

                decorateObject(designerImage, "image");
                designerImage.set({
                    assetPath: assetPath ?? undefined,
                    left: area.x + area.width / 2,
                    layerName,
                    top: area.y + area.height / 2,
                    originX: "center",
                    originY: "center",
                });

                designerImage.scaleToWidth(area.width * 0.72);

                if (designerImage.getScaledHeight() > area.height * 0.82) {
                    designerImage.scaleToHeight(area.height * 0.82);
                }

                state.canvas?.add(designerImage);
                state.canvas?.setActiveObject(designerImage);
                renderCanvas();
                resolve();
            },
            {
                crossOrigin: "anonymous",
            },
        );
    });
}

function initAiProviderSelect(): void {
    if (
        !elements.aiPromptInput
        || !elements.aiStyleSelect
        || !elements.aiProviderSelect
        || !elements.generateAiButton
    ) {
        return;
    }

    const featureEnabled = bootstrap.aiImage?.enabled === true;
    const providers = Array.isArray(bootstrap.aiImage?.providers) ? bootstrap.aiImage.providers : [];
    const preferredProvider = providers.find((provider) => {
        return provider.value === bootstrap.aiImage?.defaultProvider && provider.available;
    });
    const firstAvailableProvider = providers.find((provider) => provider.available);
    const resolvedProvider = preferredProvider?.value ?? firstAvailableProvider?.value ?? null;

    elements.aiPromptInput.disabled = !featureEnabled;
    elements.aiStyleSelect.disabled = !featureEnabled;
    elements.aiProviderSelect.disabled = !featureEnabled || firstAvailableProvider === undefined;
    elements.generateAiButton.disabled = !featureEnabled || firstAvailableProvider === undefined;

    if (resolvedProvider !== null) {
        elements.aiProviderSelect.value = resolvedProvider;
    }
}

function resolveSelectedAiProvider(): AiImageProvider | null {
    if (!elements.aiProviderSelect) {
        return null;
    }

    const selectedOption = elements.aiProviderSelect.selectedOptions.item(0);

    if (selectedOption?.disabled) {
        return null;
    }

    const value = elements.aiProviderSelect.value;

    return value === "chatgpt" || value === "nano-banana" ? value : null;
}

function collectAiReferencePaths(): string[] {
    if (!state.canvas) {
        return [];
    }

    const paths = new Set<string>();

    state.canvas.getObjects().forEach((object) => {
        const designerObject = object as DesignerObject;
        const assetPath = typeof designerObject.assetPath === "string" ? designerObject.assetPath.trim() : "";
        const fileName = assetPath.split("/").pop() ?? "";

        if (
            designerObject.mode === state.mode
            && designerObject.layerType === "image"
            && assetPath !== ""
            && !fileName.toLowerCase().startsWith("ai-")
        ) {
            paths.add(assetPath);
        }
    });

    return Array.from(paths).slice(0, 4);
}

function decorateObject(object: DesignerObject, layerType?: LayerType): void {
    const resolvedLayerType = layerType ?? object.layerType ?? inferLayerType(object);

    object.set({
        borderColor: "#d66d42",
        cornerColor: "#d66d42",
        cornerStyle: "circle",
        cornerSize: 14,
        layerId: object.layerId ?? createLayerId(),
        layerName: object.layerName ?? undefined,
        layerType: resolvedLayerType,
        minScaleLimit: resolveMinScaleLimit(object, resolvedLayerType),
        mode: object.mode ?? state.mode,
        padding: 8,
        transparentCorners: false,
    });
}

function resolveMinScaleLimit(object: DesignerObject, layerType: LayerType): number {
    const referenceSize = Math.max(Number(object.width ?? 0), Number(object.height ?? 0), 1);
    const minimumVisibleSize = layerType === "image" ? 24 : 32;

    return Math.min(0.2, minimumVisibleSize / referenceSize);
}

function handleSelectionChange(event: FabricSelectionEvent): void {
    state.selectedObject = (event.selected?.[0] as DesignerObject | undefined) ?? null;
    syncSelectionControls();
    refreshLayerList();
}

function clearSelection(): void {
    state.selectedObject = null;
    syncSelectionControls();
    refreshLayerList();
}

function syncSelectionControls(): void {
    if (!state.selectedObject) {
        elements.selectionEmpty.classList.remove("hidden");
        elements.selectionControls.classList.add("hidden");
        return;
    }

    const isText = isTextSelection();

    elements.selectionEmpty.classList.add("hidden");
    elements.selectionControls.classList.remove("hidden");
    elements.selectedLayerType.textContent = isText ? t("textLayerBadge") : t("imageLayerBadge");
    elements.textFieldWrap.classList.toggle("hidden", !isText);
    elements.fontFamilyWrap.classList.toggle("hidden", !isText);
    elements.textStyleGroup.classList.toggle("hidden", !isText);

    if (isText) {
        elements.layerTextInput.value = state.selectedObject.text ?? "";
        elements.fontFamilySelect.value = state.selectedObject.fontFamily ?? "Trebuchet MS";
        elements.fillColorInput.value = normalizeColor(state.selectedObject.fill ?? "#101828");
        elements.fontSizeInput.value = String(Math.round(state.selectedObject.fontSize ?? 34));
    }

    elements.opacityInput.value = String(Math.round((state.selectedObject.opacity ?? 1) * 100));
    elements.rotationInput.value = String(Math.round(state.selectedObject.angle ?? 0));
}

function refreshLayerList(): void {
    const objects = state.canvas
        ? state.canvas
              .getObjects()
              .filter((object) => (object as DesignerObject).mode === state.mode)
              .slice()
              .reverse() as DesignerObject[]
        : [];

    if (!objects.length) {
        elements.layersList.innerHTML = `
            <div class="empty-card">
                ${escapeHtml(t("sideEmpty"))}
            </div>
        `;
        return;
    }

    elements.layersList.innerHTML = objects
        .map((object) => {
            const activeClass =
                state.selectedObject?.layerId === object.layerId ? " active" : "";

            return `
                <article class="layer-card${activeClass}">
                    <button type="button" class="layer-main" data-layer-id="${object.layerId}">
                        <span class="layer-title">${escapeHtml(resolveLayerTitle(object))}</span>
                        <span class="layer-subtitle">${escapeHtml(layerTypeLabel(object.layerType ?? inferLayerType(object)))}</span>
                    </button>
                    <div class="layer-actions">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            title="${escapeHtml(t("duplicateLayerTitle"))}"
                            data-action="duplicate"
                            data-layer-id="${object.layerId}"
                        >
                            ${escapeHtml(t("copyButton"))}
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            title="${escapeHtml(t("removeLayerTitle"))}"
                            data-action="remove"
                            data-layer-id="${object.layerId}"
                        >
                            ${escapeHtml(t("removeButton"))}
                        </button>
                    </div>
                </article>
            `;
        })
        .join("");
}

function syncArtworkSummary(): void {
    const counts = {
        front: 0,
        back: 0,
    };

    state.canvas?.getObjects().forEach((object) => {
        if ((object as DesignerObject).type !== "image") {
            return;
        }

        const mode = (object as DesignerObject).mode === "back" ? "back" : "front";
        counts[mode] += 1;
    });

    elements.artworkCountFront.textContent = String(counts.front);
    elements.artworkCountBack.textContent = String(counts.back);
    elements.artworkCountTotal.textContent = String(counts.front + counts.back);
}

function renderCatalog(): void {
    elements.catalogCards.innerHTML = state.products
        .map((product) => {
            const activeClass = state.product?.key === product.key ? " active" : "";
            const previewColor = product.colors[0] ?? null;

            return `
                <button type="button" class="catalog-card${activeClass}" data-product-key="${product.key}">
                    <div class="catalog-preview">
                        <img loading="lazy" decoding="async" src="${resolveCatalogPreviewUrl(product, previewColor, "front")}" alt="${escapeHtml(t("catalogPreviewFrontAlt", { name: product.name }))}">
                        <img loading="lazy" decoding="async" src="${resolveCatalogPreviewUrl(product, previewColor, "back")}" alt="${escapeHtml(t("catalogPreviewBackAlt", { name: product.name }))}">
                    </div>
                    <div>
                        <span class="catalog-title">${escapeHtml(product.name)}</span>
                        <span class="catalog-copy">${escapeHtml(product.description)}</span>
                        <span class="catalog-price">${escapeHtml(t("catalogPriceFrom", { price: product.basePrice.toFixed(2) }))}</span>
                        <span class="catalog-fee">${escapeHtml(t("catalogImageFee"))}</span>
                        <span class="catalog-cta">${escapeHtml(t("catalogSelectButton"))}</span>
                    </div>
                </button>
            `;
        })
        .join("");
}

function renderColorSwatches(): void {
    if (!state.product) {
        elements.colorSwatches.innerHTML = "";
        return;
    }

    if (!state.product.colors.length) {
        elements.colorSwatches.innerHTML = `
            <div class="empty-card">${escapeHtml(t("noColorwaysConfigured"))}</div>
        `;
        return;
    }

    elements.colorSwatches.innerHTML = state.product.colors
        .map((color) => {
            const activeClass = state.color?.key === color.key ? " active" : "";

            return `
                <button type="button" class="swatch-button${activeClass}" data-color-key="${color.key}">
                    <span class="swatch-dot" style="background:${color.hex}"></span>
                    <span class="swatch-label">${escapeHtml(color.name)}</span>
                </button>
            `;
        })
        .join("");
}

async function saveProject(): Promise<void> {
    if (!state.canvas || !state.product) {
        return;
    }

    if (!state.color) {
        showToast(t("noColorwaysConfigured"), true);
        return;
    }

    if (!requireAuthenticatedForProjects()) {
        return;
    }

    const name = elements.projectNameInput.value.trim();

    if (!name) {
        showToast(t("projectNameRequired"), true);
        return;
    }

    elements.saveProjectButton.disabled = true;
    elements.saveProjectButton.textContent = t("savingProjectButton");

    try {
        const [frontPreview, backPreview] = await Promise.all([
            captureMode("front"),
            captureMode("back"),
        ]);
        const [frontDesign, backDesign] = await Promise.all([
            capturePrintableMode("front"),
            capturePrintableMode("back"),
        ]);

        const payload = await request<ProjectItemResponse>(bootstrap.api.projects, {
            method: "POST",
            body: JSON.stringify({
                id: state.currentProjectId,
                name,
                productKey: state.product.key,
                colorKey: state.color.key,
                canvasJson: JSON.stringify(state.canvas.toJSON(layerProps)),
                frontPreview,
                backPreview,
                frontDesign,
                backDesign,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });

        state.currentProjectId = payload.project.id;
        elements.projectNameInput.value = payload.project.name;
        syncDesignerUrl({
            projectId: payload.project.id,
        });
        showToast(t("projectSaved"));
    } catch (error) {
        showToast(asErrorMessage(error), true);
    } finally {
        elements.saveProjectButton.disabled = false;
        elements.saveProjectButton.textContent = t("saveProjectButton");
    }
}

async function addCurrentProjectToCart(): Promise<void> {
    if (!requireAuthenticatedForProjects()) {
        return;
    }

    if (!state.currentProjectId) {
        showToast(t("saveFirstBeforeCart"), true);
        return;
    }

    await addProjectToCart(state.currentProjectId);
}

async function addProjectToCart(projectId: string | number): Promise<void> {
    if (!requireAuthenticatedForProjects()) {
        return;
    }

    try {
        const payload = await request<MessageResponse>(`${bootstrap.api.cartProjects}/${projectId}`, {
            method: "POST",
        });
        showToast(payload.message || t("designAddedToCart"));
    } catch (error) {
        showToast(asErrorMessage(error), true);
    }
}

async function loadProject(projectId: string): Promise<void> {
    if (!state.canvas) {
        return;
    }

    try {
        const payload = await request<ProjectItemResponse>(`${bootstrap.api.projects}/${projectId}`);
        const project = payload.project;
        const canvasJson = project.canvasJson ? JSON.parse(project.canvasJson) : {};

        state.currentProjectId = project.id;
        elements.projectNameInput.value = project.name;
        state.mode = "front";
        showEditorView({
            syncUrl: false,
        });
        updateModeButtons();
        setProduct(project.productKey, {
            preferredColorKey: project.colorKey,
            repositionLayers: false,
            syncUrl: false,
        });

        state.canvas.clear();
        state.canvas.loadFromJSON(canvasJson, () => {
            state.canvas?.getObjects().forEach((object) => {
                decorateObject(object as DesignerObject);
            });
            constrainCanvasObjectsToPrintArea();
            state.selectedObject = null;
            syncSelectionControls();
            applyBackground();
            applyModeVisibility();
            syncDesignerUrl({
                projectId: project.id,
            });
            showToast(t("projectLoaded"));
        });
    } catch (error) {
        showToast(asErrorMessage(error), true);
    }
}

function resetDesign(): void {
    if (
        state.canvas &&
        state.canvas.getObjects().length &&
        !window.confirm(t("newDesignConfirm"))
    ) {
        return;
    }

    state.currentProjectId = null;
    state.selectedObject = null;
    elements.projectNameInput.value = "";
    state.canvas?.clear();
    syncSelectionControls();
    applyBackground();
    applyModeVisibility();
    syncDesignerUrl();
    showToast(t("canvasReset"));
}

async function downloadPreview(): Promise<void> {
    if (!state.product) {
        return;
    }

    try {
        const dataUrl = await captureMode(state.mode);
        const link = document.createElement("a");
        const fileName = sanitizeFileName(
            `${elements.projectNameInput.value || state.product.name}-${state.mode}.png`,
        );

        link.href = dataUrl;
        link.download = fileName;
        link.click();
    } catch (error) {
        showToast(asErrorMessage(error), true);
    }
}

function captureMode(mode: DesignerMode): Promise<string> {
    if (!state.canvas || !state.product || !state.color) {
        return Promise.reject(new Error(t("canvasNotReady")));
    }

    const canvas = state.canvas;
    const product = state.product;
    const color = state.color;

    return new Promise((resolve, reject) => {
        const captureCanvas = new fabric.StaticCanvas(null, {
            width: canvasBaseWidth,
            height: canvasBaseHeight,
        });
        const background = resolveCanvasBackground(
            product as DesignerProduct,
            color as DesignerColor,
            mode,
            canvasBaseWidth,
            canvasBaseHeight,
        );

        captureCanvas.loadFromJSON(canvas.toJSON(layerProps), () => {
            captureCanvas.getObjects().forEach((object) => {
                (object as DesignerObject).visible = (object as DesignerObject).mode === mode;
            });

            captureCanvas.setBackgroundImage(
                background.src,
                () => {
                    try {
                        captureCanvas.requestRenderAll();
                        const dataUrl = exportCanvasDataUrl(captureCanvas, {
                            format: "jpeg",
                            quality: 0.82,
                            multiplier: 0.78,
                        });

                        captureCanvas.dispose();
                        resolve(dataUrl);
                    } catch (error) {
                        captureCanvas.dispose();
                        reject(error);
                    }
                },
                background.options,
            );
        });
    });
}

function capturePrintableMode(mode: DesignerMode): Promise<string | null> {
    if (!state.canvas) {
        return Promise.reject(new Error(t("canvasNotReady")));
    }

    const modeObjects = state.canvas
        .getObjects()
        .filter((object) => (object as DesignerObject).mode === mode);

    if (!modeObjects.length) {
        return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
        const captureCanvas = new fabric.StaticCanvas(null, {
            width: canvasBaseWidth,
            height: canvasBaseHeight,
        });

        captureCanvas.loadFromJSON(state.canvas?.toJSON(layerProps) ?? {}, () => {
            captureCanvas.getObjects().forEach((object) => {
                (object as DesignerObject).visible = (object as DesignerObject).mode === mode;
            });

            // Print exports contain artwork only; product photographs are preview backgrounds.
            captureCanvas.backgroundImage = null;
            captureCanvas.backgroundColor = "";

            try {
                captureCanvas.requestRenderAll();
                const dataUrl = exportCanvasDataUrl(captureCanvas, {
                    format: "png",
                    quality: 1,
                });

                captureCanvas.dispose();
                resolve(dataUrl);
            } catch (error) {
                captureCanvas.dispose();
                reject(error);
            }
        });
    });
}

function syncCanvasViewport(): void {
    if (!state.canvas) {
        return;
    }

    const availableWidth = Math.max(Math.floor(elements.canvasStage.clientWidth - 8), 260);
    const scale = availableWidth / canvasBaseWidth;
    const scaledHeight = Math.round(canvasBaseHeight * scale);

    state.canvas.setDimensions({
        width: availableWidth,
        height: scaledHeight,
    });
    state.canvas.setViewportTransform([scale, 0, 0, scale, 0, 0]);
    state.canvas.calcOffset();
    updatePrintAreaGuide();
    renderCanvas();
}

function selectLayer(layerId: string): void {
    if (!state.canvas) {
        return;
    }

    const object = findLayer(layerId);

    if (!object) {
        return;
    }

    state.canvas.setActiveObject(object);
    state.selectedObject = object;
    renderCanvas();
    syncSelectionControls();
    refreshLayerList();
}

function duplicateLayer(layerId: string): void {
    if (!state.canvas) {
        return;
    }

    const object = findLayer(layerId);

    if (!object) {
        return;
    }

    object.clone((clone) => {
        const duplicated = clone as DesignerObject;

        decorateObject(duplicated, object.layerType);
        duplicated.set({
            left: (object.left ?? 0) + 16,
            top: (object.top ?? 0) + 16,
            mode: object.mode,
        });
        state.canvas?.add(duplicated);
        constrainObjectToPrintArea(duplicated);
        state.canvas?.setActiveObject(duplicated);
        renderCanvas();
    });
}

function removeLayer(layerId: string): void {
    if (!state.canvas) {
        return;
    }

    const object = findLayer(layerId);

    if (!object) {
        return;
    }

    state.canvas.remove(object);
    state.selectedObject = null;
    syncSelectionControls();
    renderCanvas();
}

function findLayer(layerId: string): DesignerObject | null {
    return (
        state.canvas
            ?.getObjects()
            .find((object) => String((object as DesignerObject).layerId) === String(layerId)) as
            | DesignerObject
            | undefined
    ) ?? null;
}

function isTextSelection(): boolean {
    return !!state.selectedObject && inferLayerType(state.selectedObject) === "text";
}

function inferLayerType(object: DesignerObject): LayerType {
    return object.type === "image" ? "image" : "text";
}

function layerTypeLabel(layerType: LayerType): string {
    return layerType === "image" ? t("layerTypeImage") : t("layerTypeText");
}

function resolveLayerTitle(object: DesignerObject): string {
    if (inferLayerType(object) === "image") {
        const imageLabel = (object.layerName ?? t("defaultArtworkLayerName")).trim();
        return imageLabel.length > 28 ? `${imageLabel.slice(0, 28)}...` : imageLabel;
    }

    const content = (object.text ?? t("defaultTextLayerName")).trim();
    return content.length > 28 ? `${content.slice(0, 28)}...` : content;
}

function getPrintArea(): DesignerProduct["printArea"] {
    return state.product?.printArea ?? { x: 150, y: 180, width: 220, height: 260 };
}

function repositionLayersForPrintArea(
    previousArea: DesignerProduct["printArea"],
    nextArea: DesignerProduct["printArea"],
): void {
    if (!state.canvas) {
        return;
    }

    const deltaX = nextArea.x - previousArea.x;
    const deltaY = nextArea.y - previousArea.y;

    state.canvas.getObjects().forEach((object) => {
        const designerObject = object as DesignerObject;

        designerObject.set({
            left: Number(designerObject.left ?? 0) + deltaX,
            top: Number(designerObject.top ?? 0) + deltaY,
        });
        constrainObjectToPrintArea(designerObject, nextArea);
    });
}

function constrainCanvasObjectsToPrintArea(area = getPrintArea()): void {
    if (!state.canvas) {
        return;
    }

    state.canvas.getObjects().forEach((object) => {
        constrainObjectToPrintArea(object as DesignerObject, area);
    });
}

function constrainObjectToPrintArea(
    object: DesignerObject,
    area = getPrintArea(),
): void {
    if (
        typeof object.left !== "number"
        || typeof object.top !== "number"
        || typeof object.getBoundingRect !== "function"
    ) {
        return;
    }

    const bounds = object.getBoundingRect(true, true);
    const areaRight = area.x + area.width;
    const areaBottom = area.y + area.height;
    let nextLeft = object.left;
    let nextTop = object.top;

    if (bounds.width >= area.width) {
        nextLeft += area.x + (area.width - bounds.width) / 2 - bounds.left;
    } else {
        if (bounds.left < area.x) {
            nextLeft += area.x - bounds.left;
        }

        if (bounds.left + bounds.width > areaRight) {
            nextLeft += areaRight - (bounds.left + bounds.width);
        }
    }

    if (bounds.height >= area.height) {
        nextTop += area.y + (area.height - bounds.height) / 2 - bounds.top;
    } else {
        if (bounds.top < area.y) {
            nextTop += area.y - bounds.top;
        }

        if (bounds.top + bounds.height > areaBottom) {
            nextTop += areaBottom - (bounds.top + bounds.height);
        }
    }

    object.set({
        left: nextLeft,
        top: nextTop,
    });
    object.setCoords();
}

function updatePrintAreaGuide(): void {
    if (!state.canvas || !state.product) {
        elements.printAreaGuide.hidden = true;
        return;
    }

    const canvasWidth = state.canvas.getWidth();
    const scale = canvasWidth / canvasBaseWidth;

    if (!Number.isFinite(scale) || scale <= 0) {
        elements.printAreaGuide.hidden = true;
        return;
    }

    const area = getPrintArea();

    elements.printAreaGuide.hidden = false;
    elements.printAreaGuide.style.left = `${area.x * scale}px`;
    elements.printAreaGuide.style.top = `${area.y * scale}px`;
    elements.printAreaGuide.style.width = `${area.width * scale}px`;
    elements.printAreaGuide.style.height = `${area.height * scale}px`;
}

function renderCanvas(): void {
    state.canvas?.requestRenderAll();
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const headers = new Headers(options.headers ?? {});
    headers.set("Accept", "application/json");

    if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }

    const response = await fetch(url, {
        method: options.method ?? "GET",
        headers,
        body: options.body,
    });

    const rawBody = await response.text();
    let payload: Record<string, unknown> = {};

    if (rawBody.trim() !== "") {
        try {
            payload = JSON.parse(rawBody) as Record<string, unknown>;
        } catch {
            payload = {
                message: rawBody,
            };
        }
    }

    if (!response.ok) {
        throw new Error(resolveErrorMessage(payload, response.status, response.statusText) || t("requestFailed"));
    }

    return payload as T;
}

function resolveErrorMessage(payload: Record<string, unknown>, status?: number, statusText?: string): string {
    const messages = payload.messages;

    if (messages && typeof messages === "object" && !Array.isArray(messages)) {
        return Object.values(messages as Record<string, unknown>)
            .map((value) => String(value))
            .join(" ");
    }

    if (typeof payload.message === "string") {
        return simplifyErrorMessage(payload.message);
    }

    if (typeof payload.error === "string") {
        return simplifyErrorMessage(payload.error);
    }

    if (status === 413) {
        return t("requestTooLarge");
    }

    if (typeof status === "number" && status >= 500) {
        return t("serverError", {
            status,
            suffix: statusText ? `: ${statusText}` : "",
        });
    }

    return "";
}

function simplifyErrorMessage(value: string): string {
    const trimmed = value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

    if (trimmed === "") {
        return t("requestFailed");
    }

    if (trimmed.length <= 220) {
        return trimmed;
    }

    return `${trimmed.slice(0, 217)}...`;
}

function formatTimestamp(value: string): string {
    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(currentLanguage() === "ms" ? "ms-MY" : "en", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(parsed);
}

function showToast(message: string, isError = false): void {
    elements.toast.textContent = message;
    elements.toast.classList.add("visible");
    elements.toast.classList.toggle("error", isError);

    if (state.toastTimer !== null) {
        window.clearTimeout(state.toastTimer);
    }

    state.toastTimer = window.setTimeout(() => {
        elements.toast.classList.remove("visible");
        elements.toast.classList.remove("error");
    }, 2800);
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function sanitizeFileName(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function normalizeColor(value: unknown): string {
    if (typeof value !== "string") {
        return "#101828";
    }

    if (value.startsWith("#")) {
        return value;
    }

    const matches = value.match(/\d+/g);

    if (!matches || matches.length < 3) {
        return "#101828";
    }

    return rgbToHex({
        r: Number(matches[0]),
        g: Number(matches[1]),
        b: Number(matches[2]),
    });
}

function rgbToHex(color: { r: number; g: number; b: number }): string {
    return `#${[color.r, color.g, color.b]
        .map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0"))
        .join("")}`;
}

function createLayerId(): string {
    return `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function exportCanvasDataUrl(
    canvas: { toDataURL: (exportOptions: Record<string, unknown>) => string },
    options: Record<string, unknown>,
): string {
    return (canvas as unknown as { toDataURL: (exportOptions: Record<string, unknown>) => string }).toDataURL(options);
}

function asErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : t("requestFailed");
}

function requireAuthenticatedForProjects(): boolean {
    if (bootstrap.customer.isAuthenticated) {
        return true;
    }

    showToast(t("authRequiredDesigner"), true);
    window.setTimeout(() => {
        window.location.href = bootstrap.customer.loginUrl;
    }, 700);

    return false;
}

async function loadRequestedProjectFromUrl(): Promise<void> {
    if (!bootstrap.customer.isAuthenticated) {
        return;
    }

    const requestedProjectId = getRequestedProjectId();

    if (!requestedProjectId || state.currentProjectId === requestedProjectId) {
        return;
    }

    await loadProject(String(requestedProjectId));
}

function syncDesignerUrl(
    options: {
        projectId?: number | null;
    } = {},
): void {
    const url = new URL(window.location.href);
    const nextProjectId = options.projectId ?? null;

    if (state.view === "editor") {
        url.searchParams.set("view", "editor");
    } else {
        url.searchParams.delete("view");
    }

    if (state.product) {
        url.searchParams.set("product", state.product.key);
    } else {
        url.searchParams.delete("product");
    }

    if (state.color) {
        url.searchParams.set("color", state.color.key);
    } else {
        url.searchParams.delete("color");
    }

    if (nextProjectId !== null) {
        url.searchParams.set("project", String(nextProjectId));
    } else {
        url.searchParams.delete("project");
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (nextUrl !== currentUrl) {
        window.history.replaceState({}, "", nextUrl);
    }
}

function getRequestedView(): DesignerView {
    const params = new URLSearchParams(window.location.search);
    return params.get("view") === "editor" ? "editor" : "catalog";
}

function getRequestedProductKey(): string | null {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("product");

    if (typeof value !== "string") {
        return null;
    }

    const normalized = value.trim();
    return normalized === "" ? null : normalized;
}

function getRequestedColorKey(): string | null {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("color");

    if (typeof value !== "string") {
        return null;
    }

    const normalized = value.trim();
    return normalized === "" ? null : normalized;
}

function getRequestedProjectId(): number | null {
    const params = new URLSearchParams(window.location.search);
    const projectValue = params.get("project");
    const projectId = Number(projectValue);

    if (!projectValue || !Number.isInteger(projectId) || projectId <= 0) {
        return null;
    }

    return projectId;
}

function findProductByKey(productKey: string | null): DesignerProduct | null {
    if (!productKey) {
        return null;
    }

    return state.products.find((product) => product.key === productKey) ?? null;
}

function parseBootstrapPayload(element: HTMLElement): DesignerBootstrap {
    return JSON.parse(element.textContent ?? "{}") as DesignerBootstrap;
}

function requireElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);

    if (!(element instanceof HTMLElement)) {
        throw new Error(`Missing designer element: #${id}`);
    }

    return element as T;
}

function findElement<T extends HTMLElement>(id: string): T | null {
    const element = document.getElementById(id);

    return element instanceof HTMLElement ? (element as T) : null;
}

function getElements(): DesignerElements {
    return {
        addTextButton: requireElement<HTMLButtonElement>("add-text-btn"),
        artworkCountBack: requireElement<HTMLSpanElement>("artwork-count-back"),
        artworkCountFront: requireElement<HTMLSpanElement>("artwork-count-front"),
        artworkCountTotal: requireElement<HTMLSpanElement>("artwork-count-total"),
        addToCartButton: requireElement<HTMLButtonElement>("add-to-cart-btn"),
        aiProviderSelect: findElement<HTMLSelectElement>("ai-provider-select"),
        aiPromptInput: findElement<HTMLTextAreaElement>("ai-prompt-input"),
        aiStyleSelect: findElement<HTMLSelectElement>("ai-style-select"),
        backToCatalogButton: requireElement<HTMLButtonElement>("back-to-catalog-btn"),
        bringForwardButton: requireElement<HTMLButtonElement>("bring-forward-btn"),
        catalogView: requireElement<HTMLElement>("designer-catalog-view"),
        canvasStage: requireElement<HTMLDivElement>("canvas-stage"),
        catalogCards: requireElement<HTMLDivElement>("catalog-cards"),
        colorSwatches: requireElement<HTMLDivElement>("color-swatches"),
        currentColorLabel: requireElement<HTMLSpanElement>("current-color-label"),
        currentModeLabel: requireElement<HTMLSpanElement>("current-mode-label"),
        currentProductLabel: requireElement<HTMLSpanElement>("current-product-label"),
        downloadPreviewButton: requireElement<HTMLButtonElement>("download-preview-btn"),
        duplicateLayerButton: requireElement<HTMLButtonElement>("duplicate-layer-btn"),
        editorView: requireElement<HTMLElement>("designer-editor-view"),
        fillColorInput: requireElement<HTMLInputElement>("fill-color-input"),
        fontFamilySelect: requireElement<HTMLSelectElement>("font-family-select"),
        fontFamilyWrap: requireElement<HTMLDivElement>("font-family-wrap"),
        fontSizeInput: requireElement<HTMLInputElement>("font-size-input"),
        fontSizeWrap: requireElement<HTMLDivElement>("font-size-wrap"),
        generateAiButton: findElement<HTMLButtonElement>("generate-ai-btn"),
        imageUploadInput: requireElement<HTMLInputElement>("image-upload-input"),
        layerTextInput: requireElement<HTMLTextAreaElement>("layer-text-input"),
        languageSelect: findElement<HTMLSelectElement>("designer-language-select"),
        layersList: requireElement<HTMLDivElement>("layers-list"),
        modeButtons: Array.from(document.querySelectorAll<HTMLButtonElement>(".mode-button")),
        newProjectButton: requireElement<HTMLButtonElement>("new-project-btn"),
        opacityInput: requireElement<HTMLInputElement>("opacity-input"),
        pageHeader: requireElement<HTMLElement>("designer-header"),
        printAreaGuide: requireElement<HTMLDivElement>("print-area-guide"),
        projectNameInput: requireElement<HTMLInputElement>("project-name-input"),
        removeLayerButton: requireElement<HTMLButtonElement>("remove-layer-btn"),
        rotationInput: requireElement<HTMLInputElement>("rotation-input"),
        saveProjectButton: requireElement<HTMLButtonElement>("save-project-btn"),
        selectedLayerType: requireElement<HTMLSpanElement>("selected-layer-type"),
        selectionControls: requireElement<HTMLDivElement>("selection-controls"),
        selectionEmpty: requireElement<HTMLDivElement>("selection-empty"),
        sendBackwardButton: requireElement<HTMLButtonElement>("send-backward-btn"),
        textFieldWrap: requireElement<HTMLDivElement>("text-field-wrap"),
        textStyleGroup: requireElement<HTMLDivElement>("text-style-group"),
        toast: requireElement<HTMLDivElement>("designer-toast"),
    };
}

function initLanguageSelect(): void {
    const bootstrapLanguage = normalizeLanguageValue(bootstrap.locale);
    const urlLanguage = getUrlLanguage();
    const initialLanguage = urlLanguage ?? bootstrapLanguage;

    if (elements.languageSelect) {
        elements.languageSelect.value = initialLanguage;
    }

    document.documentElement.lang = initialLanguage;
}

function applyLanguageSelection(
    value: string,
    options: {
        reload?: boolean;
    } = {},
): void {
    const language = normalizeLanguageValue(value);

    if (elements.languageSelect) {
        elements.languageSelect.value = language;
    }

    document.documentElement.lang = language;

    if (options.reload !== false) {
        redirectWithLanguage(language);
    }
}

function isSupportedLanguage(value: string): value is DesignerLanguage {
    return (supportedLanguages as readonly string[]).includes(value);
}

function normalizeLanguageValue(value: string | null | undefined): DesignerLanguage {
    const normalized = (value ?? "").trim().toLowerCase();

    return normalized.startsWith("ms") ? "ms" : defaultLanguage;
}

function getUrlLanguage(): DesignerLanguage | null {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("lang");

    return value === null ? null : normalizeLanguageValue(value);
}

function redirectWithLanguage(language: DesignerLanguage): void {
    const url = new URL(window.location.href);

    url.searchParams.set("lang", language);
    window.location.href = url.toString();
}

function currentLanguage(): DesignerLanguage {
    return normalizeLanguageValue(document.documentElement.lang || bootstrap.locale);
}

function t(key: string, replacements: Record<string, string | number> = {}): string {
    const template = bootstrap.i18n?.[key] ?? key;

    return Object.entries(replacements).reduce((result, [token, replacement]) => {
        return result.split(`{${token}}`).join(String(replacement));
    }, template);
}

function closestFromEvent<T extends Element>(event: Event, selector: string): T | null {
    const target = event.target;

    if (!(target instanceof Element)) {
        return null;
    }

    return target.closest(selector) as T | null;
}
